import { Context, Effect, Stream, SubscriptionRef } from 'effect'
import type { AppRoute } from '@features/routes'
import { toPath } from '@features/routes'
import type { RouteLocation } from '~/features/routing/location-change'
import { sanitizeUrlForNavigation } from '~/util/safe-url'
import { match, P } from '@st/pattern'

type NavigationGuard = { message: string }

export type RoutingService = {
  currentLocation: Effect.Effect<RouteLocation>
  locations: Stream.Stream<RouteLocation>
  navigate: (route: AppRoute) => Effect.Effect<null>
  openRoute: (route: AppRoute) => Effect.Effect<null>
  openUrl: (url: string) => Effect.Effect<null>
  redirectTo: (url: string) => Effect.Effect<null>
  pushPath: (pathname: string) => Effect.Effect<null>
  replacePath: (pathname: string) => Effect.Effect<null>
  /** Holds the guard until the caller's task is interrupted (saved or discarded). */
  guardNavigation: (guard: NavigationGuard) => Effect.Effect<never>
}

export class Routing extends Context.Service<Routing, RoutingService>()('Routing') {}

/**
 * The current browser location followed by every later location change.
 *
 * The physical service owns browser callbacks and their scope. Feature modules
 * consume only this Effect-native stream through Task definitions.
 */
export const observeLocations = Stream.unwrap(
  Effect.map(Effect.service(Routing), (routing) => routing.locations)
)

function readBrowserLocation(): RouteLocation {
  return {
    origin: window.location.origin,
    pathname: window.location.pathname + window.location.search
  }
}

/**
 * The browser navigation capability and its change feed share one source of
 * truth. Owned navigation updates that source immediately; browser back and
 * forward events update it through one scoped platform stream. This is the
 * same guarantee Elm's navigation key provides, without patching global
 * history methods or inventing DOM events.
 */
export const makeBrowserRouting = Effect.gen(function* () {
  const location = yield* SubscriptionRef.make(readBrowserLocation())
  let guard: NavigationGuard | null = null
  let historyIndex: number = window.history.state?.stNavigationIndex ?? 0
  let restoringHistory = false
  window.history.replaceState({ ...window.history.state, stNavigationIndex: historyIndex }, '')

  const canLeave = () =>
    !restoringHistory &&
    match(guard)
      .with(null, () => true)
      .with({ message: P.string }, ({ message }) => window.confirm(message))
      .exhaustive()
  const publishBrowserLocation = Effect.sync(readBrowserLocation).pipe(
    Effect.flatMap((next) => SubscriptionRef.set(location, next))
  )

  yield* Stream.fromEventListener<PopStateEvent>(window, 'popstate').pipe(
    Stream.runForEach((event) =>
      Effect.gen(function* () {
        if (restoringHistory) {
          restoringHistory = false
          return
        }
        const next = readBrowserLocation()
        const current = yield* SubscriptionRef.get(location)
        const nextIndex: number = event.state?.stNavigationIndex ?? historyIndex - 1
        if (next.pathname == current.pathname) {
          // Repeated URLs still occupy distinct browser history positions.
          historyIndex = nextIndex
          return
        }
        if (!canLeave()) {
          // popstate is not cancellable. Restore the old history position without
          // publishing the rejected URL, so the folder and its edits stay mounted.
          // For A(0) -> B(1), rejecting Back runs go(1); its popstate is swallowed.
          restoringHistory = true
          window.history.go(historyIndex - nextIndex)
          return
        }
        historyIndex = nextIndex
        yield* publishBrowserLocation
      })
    ),
    Effect.forkScoped({ startImmediately: true })
  )

  yield* Stream.fromEventListener<BeforeUnloadEvent>(window, 'beforeunload').pipe(
    Stream.runForEach((event) =>
      Effect.sync(() => {
        if (!guard) return
        event.preventDefault()
        event.returnValue = ''
      })
    ),
    Effect.forkScoped({ startImmediately: true })
  )

  const pushPath = (pathname: string) =>
    Effect.gen(function* () {
      if (!canLeave()) return null
      historyIndex += 1
      window.history.pushState({ stNavigationIndex: historyIndex }, '', pathname)
      document.body.scrollTop = 0
      yield* publishBrowserLocation
      return null
    })

  const replacePath = (pathname: string) =>
    Effect.gen(function* () {
      if (!canLeave()) return null
      window.history.replaceState(
        { ...window.history.state, stNavigationIndex: historyIndex },
        '',
        pathname
      )
      document.body.scrollTop = 0
      yield* publishBrowserLocation
      return null
    })

  return {
    currentLocation: SubscriptionRef.get(location),
    locations: SubscriptionRef.changes(location),
    navigate: (route) => pushPath(toPath(route)),
    openRoute: (route) =>
      Effect.sync(() => {
        const path = toPath(route)
        const safeUrl = sanitizeUrlForNavigation(path)
        if (safeUrl) {
          window.open(safeUrl, '_blank')
        }
        return null
      }),
    openUrl: (url) =>
      Effect.sync(() => {
        const safeUrl = sanitizeUrlForNavigation(url)
        if (safeUrl) {
          window.open(safeUrl, '_blank')
        }
        return null
      }),
    redirectTo: (url) =>
      Effect.sync(() => {
        const safeUrl = sanitizeUrlForNavigation(url)
        if (safeUrl) {
          window.location.href = safeUrl
        }
        return null
      }),
    pushPath,
    replacePath,
    guardNavigation: (nextGuard) =>
      Effect.acquireUseRelease(
        Effect.sync(() => {
          guard = nextGuard
        }),
        () => Effect.never,
        () =>
          Effect.sync(() => {
            guard = null
          })
      )
  } satisfies RoutingService
})
